import { Spinner } from '@chakra-ui/react'
import { get as _get } from 'lodash'
import { Component } from 'react'
import {
  EncodingProductionOrderRows,
  ProductionOrder,
  ProductionOrderRow,
  ProductionOrderRowDetail,
  RowColumn,
  SearchedFields
} from 'stylewhere/api'
import { Box, Modal, ModalTitle, TableColumns, TableRow, Text } from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/i18n'

interface Props {
  productionOrder: ProductionOrder
  onClose: () => void
}

interface State {
  rows: ProductionOrderRowDetail[]
  loading: boolean
  searchedFields: SearchedFields[]
  filtered: boolean
}

export class EncodingProductionOrderDetail extends Component<Props, State> {
  loadData = false

  state: State = {
    rows: [],
    loading: false,
    searchedFields: [],
    filtered: false
  }

  componentDidMount() {
    if (this.loadData) return
    this.loadData = true
    this.setState({ loading: true }, this._init)
  }

  _init = async () => {
    const { productionOrder } = this.props
    try {
      const param = {
        equalProductionOrderCodes: productionOrder.code,
        size: 500
      }
      const res = await EncodingProductionOrderRows.search<ProductionOrderRow>(param)
      if (res?.content) {
        const data: ProductionOrderRowDetail[] = []
        res.content.forEach((element) => {
          data.push({
            ...element,
            hide: false,
            totalExpectedQuantity: element.expectedQuantity + element.extraQuantity
          })
        })
        const orderedRows = EncodingExtensions.getOrderedProductionOrderRows(data)
        this.setState({ rows: orderedRows, loading: false })
      } else {
        this.setState({ rows: [], loading: false })
      }
    } catch {
      this.setState({ rows: [], loading: false })
    } finally {
      this.loadData = false
    }
  }

  getColumns = () => {
    const columns: RowColumn[] = [
      { label: __(T.misc.code), field: 'product.code', width: 200, filterable: true },
      { label: __(T.misc.style), field: 'product.style.value', width: 200, filterable: true },
      { label: __(T.misc.material), field: 'product.material.value', width: 260, filterable: true },
      { label: __(T.misc.color), field: 'product.color.value', width: 160, filterable: true },
      { label: __(T.misc.size), field: 'product.size.value', width: 120, filterable: true },
      {
        label: __(T.misc.expected_quantity),
        field: 'totalExpectedQuantity',
        width: 120,
        filterable: false,
        center: true
      },
      { label: __(T.misc.encoded_quantity), field: 'encodedQuantity', width: 120, filterable: false, center: true }
    ]
    return columns
  }

  onResetSearchField = (field: string) => {
    const { searchedFields } = this.state
    const tmp = searchedFields
    const index = tmp.findIndex((s) => s.field === field)
    if (index !== -1) {
      tmp[index].search = ''
      this.setState({ searchedFields: tmp }, this.filterRows)
    }
  }

  onSearchField = (field: string, value: string) => {
    const { searchedFields } = this.state
    const tmp = searchedFields
    const index = tmp.findIndex((s) => s.field === field)
    if (index === -1) {
      const col = this.getColumns().find((c) => c.field === field)
      tmp.push({
        field,
        search: value,
        filterType: col?.filterType ?? 1
      })
    } else {
      tmp[index].search = value
      this.setState({ searchedFields: tmp }, this.filterRows)
    }
    this.setState({ searchedFields: tmp }, this.filterRows)
  }

  filterRows = () => {
    const { searchedFields, rows } = this.state
    const tmp = rows
    const filteredSearched = searchedFields.filter((s) => s.search && s.search !== '')
    tmp.forEach((r) => {
      let cont = 0
      filteredSearched.forEach((f) => {
        const value: string = _get(r, f.field)
        if (value) {
          if (
            (f.filterType === 1 && value.toLowerCase().includes(f.search.toLowerCase())) ||
            (f.filterType === 2 && value.toLowerCase().startsWith(f.search.toLowerCase()))
          )
            cont++
        }
      })
      r.hide = cont < filteredSearched.length
    })
    this.setState({ rows: tmp, filtered: filteredSearched.length > 0 })
  }

  getMinTableWidth = (cols) => {
    let min = 0
    cols.forEach((col) => {
      min += col.width ?? 160
    })
    min += 80
    if (min > window.innerWidth) min = window.innerWidth - 60
    return min
  }

  render() {
    const { onClose } = this.props
    const { rows, loading, filtered } = this.state
    const cols = this.getColumns()
    const filteredRows = rows.filter((r) => !r.hide)
    return (
      <Modal
        closeOnOverlayClick
        visible
        onClose={onClose}
        size="6xl"
        minModalContentWidth={this.getMinTableWidth(cols)}
        bodyStyle={{ margin: '0px 0px 0px 5px', padding: 0 }}
      >
        <ModalTitle title={__(T.misc.production_order_detail)} />
        {loading && (
          <Box center vcenter mt={40} mb={40}>
            <Spinner color="#e0e0e0" size="xl" />
          </Box>
        )}

        {!loading && (filteredRows.length > 0 || filtered) && (
          <>
            <TableColumns columns={cols} onReset={this.onResetSearchField} onSearch={this.onSearchField} />
            {filteredRows.map((row, i) => (
              <TableRow key={'table-row' + i} row={row} columns={cols} />
            ))}
            {filteredRows.length === 0 && (
              <Box mt={15}>
                <Text>{__(T.messages.empty_production_order_search)}</Text>
              </Box>
            )}
          </>
        )}

        {!loading && filteredRows.length === 0 && !filtered && (
          <Box mt={20}>
            <Text>{__(T.messages.empty_production_order)}</Text>
          </Box>
        )}
      </Modal>
    )
  }
}
